import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { cn } from '../lib/utils';
import { API_BASE } from '../lib/apiBase';

// Module-level cache so the many dropdowns across the app share one fetch.
let _countriesCache = null;
let _countriesPromise = null;

async function loadCountries() {
    if (_countriesCache) return _countriesCache;
    if (!_countriesPromise) {
        const token = localStorage.getItem('token');
        _countriesPromise = axios
            .get(`${API_BASE}/api/countries`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            })
            .then((r) => {
                _countriesCache = r.data.countries || [];
                return _countriesCache;
            })
            .catch(() => {
                _countriesPromise = null;
                return [];
            });
    }
    return _countriesPromise;
}

/**
 * Shared country dropdown backed by the single Countries master.
 * - Options are the ACTIVE countries from /api/countries.
 * - A pre-existing stored value that is not an active country is still shown
 *   (as "(legacy)") so historical records never lose their value.
 * - onChange(code, countryObj) — code is the ISO alpha-2 code.
 */
export function CountrySelect({ value, onChange, testid, disabled, placeholder = 'Select country...', className }) {
    const [open, setOpen] = useState(false);
    const [countries, setCountries] = useState(_countriesCache || []);

    useEffect(() => {
        let mounted = true;
        loadCountries().then((c) => { if (mounted) setCountries(c); });
        return () => { mounted = false; };
    }, []);

    const selected = useMemo(() => {
        if (!value) return null;
        const v = String(value).toUpperCase();
        return countries.find((c) => c.code.toUpperCase() === v) || null;
    }, [value, countries]);

    const legacyLabel = !selected && value ? String(value) : null;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    data-testid={testid}
                    className={cn('w-full justify-between font-normal', className)}
                >
                    <span className={cn('truncate', !selected && !legacyLabel && 'text-muted-foreground')}>
                        {selected
                            ? `${selected.name} (${selected.code})`
                            : legacyLabel
                                ? `${legacyLabel} (legacy)`
                                : placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]" align="start">
                <Command>
                    <CommandInput placeholder="Search country..." data-testid={testid ? `${testid}-search` : undefined} />
                    <CommandList>
                        <CommandEmpty>No country found.</CommandEmpty>
                        <CommandGroup>
                            {countries.map((c) => (
                                <CommandItem
                                    key={c.code}
                                    value={`${c.name} ${c.code}`}
                                    onSelect={() => { onChange(c.code, c); setOpen(false); }}
                                    data-testid={testid ? `${testid}-opt-${c.code}` : undefined}
                                >
                                    <Check className={cn('mr-2 h-4 w-4', selected?.code === c.code ? 'opacity-100' : 'opacity-0')} />
                                    <span>{c.name}</span>
                                    <span className="ml-1 text-xs text-muted-foreground">{c.code}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default CountrySelect;
